/**
 * force-reload — rechargement instantané de l'application sur demande.
 *
 * Complément de sw-update.ts (mise à jour automatique, mais seulement quand
 * l'écran est inactif depuis 3 min). Ici le marchand décide : un tap dans
 * l'entête caisse → service workers désinscrits, caches purgés, page rechargée.
 * Le panier et la session de caisse survivent (persistés en localStorage).
 *
 * Décision pure `purgeAppCaches` séparée du `window.location.reload()` pour
 * rester testable sous vitest.
 */

export type PurgeReport = { unregistered: number; cachesCleared: number };

type CacheStorageLike = { keys: () => Promise<string[]>; delete: (k: string) => Promise<boolean> };
type SwContainerLike = {
  getRegistrations: () => Promise<ReadonlyArray<{ unregister: () => Promise<boolean> }>>;
};

export type PurgeEnv = {
  serviceWorker?: SwContainerLike;
  caches?: CacheStorageLike;
};

const defaultEnv = (): PurgeEnv => ({
  serviceWorker: typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined,
  caches: typeof caches !== 'undefined' ? caches : undefined,
});

/** Désinscrit tous les service workers et vide le Cache Storage. Ne recharge pas. */
export async function purgeAppCaches(env: PurgeEnv = defaultEnv()): Promise<PurgeReport> {
  const report: PurgeReport = { unregistered: 0, cachesCleared: 0 };
  if (env.serviceWorker) {
    const regs = await env.serviceWorker.getRegistrations();
    const results = await Promise.all(regs.map((r) => r.unregister()));
    report.unregistered = results.filter(Boolean).length;
  }
  if (env.caches) {
    const keys = await env.caches.keys();
    const results = await Promise.all(keys.map((k) => env.caches!.delete(k)));
    report.cachesCleared = results.filter(Boolean).length;
  }
  return report;
}

/** Purge puis recharge. Si la purge échoue, on recharge quand même. */
export async function forceReloadApp(): Promise<void> {
  try {
    await purgeAppCaches();
  } catch (err) {
    console.warn('[force-reload] purge partielle, rechargement quand même', err);
  }
  window.location.reload();
}

/** Version du build (package.json, injectée par vite.config.ts). */
export const APP_BUILD_VERSION: string = import.meta.env.VITE_APP_VERSION ?? 'dev';
